import React, { useState, useEffect } from 'react';
import { StaticPageHeader } from '../components/StaticPageHeader';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, FileText, Star, ShoppingCart, Loader2 } from 'lucide-react';
import { productsAPI, paymentsAPI } from '../services/api';
import { BUNDLES, SHARED_WHY_THIS_BUNDLE, buildSharedFAQs } from '../data/bundles';

/**
 * One shared layout for every individual bundle product page.
 *
 *   <BundleDetailTemplate slug="hiring-onboarding" />
 *
 * Everything that varies between bundles (name, template count, custom
 * features, preview cards, final CTA tagline) lives in /data/bundles.js.
 * "Why This Bundle?" (6 tiles) and "Frequently Asked Questions" are shared
 * verbatim so the 8 pages read consistently.
 */
export const BundleDetailTemplate = ({ slug }) => {
  const bundleData = BUNDLES[slug];
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const products = await productsAPI.getAll();
        if (!cancelled) setProduct(products.find((p) => p.id === slug) || null);
      } catch (err) {
        // Non-fatal — the page is keyed off slug, not the API response.
        console.error('Bundle product load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handlePurchase = async () => {
    if (!product) return;
    try {
      setProcessingPayment(true);
      const origin = window.location.origin;
      const checkoutData = await paymentsAPI.createProductCheckout(
        product.id,
        `${origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        `${origin}/payment-canceled`,
        { product_name: product.name, source: 'bundle_detail_page' },
      );
      window.location.href = checkoutData.url;
    } catch (err) {
      console.error('Checkout creation failed:', err);
      alert('Failed to create checkout session. Please try again.');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <StaticPageHeader />
        <div className="pt-32 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        </div>
      </div>
    );
  }
  if (!product || !bundleData) return null;

  const faqs = buildSharedFAQs(bundleData.name);

  return (
    <div className="min-h-screen bg-white">
      <StaticPageHeader />
      <div className="pt-16">
        {/* Hero */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16" style={{ backgroundImage: 'linear-gradient(135deg, #1A56E8 0%, #0f3aa3 100%)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">{product.name}</h1>
              <p className="text-xl text-blue-100 mb-2">{product.description}</p>
              <p className="text-base text-blue-100 mb-8" data-testid="bundle-template-count">
                {bundleData.templateCount} editable HR templates included.
              </p>
              <div className="flex items-baseline space-x-4 mb-6">
                <span className="text-6xl font-bold">${product.price}</span>
                <span className="text-xl text-blue-200">one-time purchase</span>
              </div>
              <Button
                size="lg"
                className="text-gray-900 font-semibold text-base px-6 min-h-[48px] max-w-full"
                style={{ backgroundColor: '#F5C100' }}
                onClick={handlePurchase}
                disabled={processingPayment}
                data-testid="bundle-purchase-cta"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 mr-2 flex-shrink-0" />
                    Purchase Now
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* What's Included */}
          <h2 className="text-3xl font-bold text-gray-900 mb-8">What's Included</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            {bundleData.features.map((feature, i) => (
              <div key={i} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700 text-lg">{feature}</span>
              </div>
            ))}
          </div>

          {/* Template Previews */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Template Previews</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {bundleData.previewItems.map((item, i) => (
                <Card key={i} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Why This Bundle — shared across all 8 pages */}
          <div className="bg-blue-50 rounded-2xl p-8 mb-16" data-testid="why-this-bundle">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why This Bundle?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {SHARED_WHY_THIS_BUNDLE.map((b, i) => (
                <div key={i} className="flex items-start space-x-3" data-testid={`why-tile-${i}`}>
                  <Star className="w-6 h-6 flex-shrink-0" style={{ color: '#F5C100' }} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{b.title}</h3>
                    <p className="text-gray-600">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ — shared across all 8 pages */}
          <div className="mb-16" data-testid="bundle-faq">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <Card key={i}>
                  <CardHeader>
                    <CardTitle className="text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center rounded-2xl p-12 text-white" style={{ backgroundImage: 'linear-gradient(135deg, #1A56E8 0%, #0f3aa3 100%)' }}>
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {bundleData.ctaTagline}
            </p>
            <Button
              size="lg"
              className="text-gray-900 font-semibold text-base px-6 min-h-[48px] max-w-full"
              style={{ backgroundColor: '#F5C100' }}
              onClick={handlePurchase}
              disabled={processingPayment}
              data-testid="bundle-final-cta"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                  Redirecting...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2 flex-shrink-0" />
                  {`Purchase Now • $${product.price}`}
                </>
              )}
            </Button>
            <p className="text-sm text-blue-100 mt-4 max-w-2xl mx-auto">
              All sales are final. Because our products are delivered digitally and downloaded immediately, DriveHR does not offer refunds. Please review the product details carefully before purchasing.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
